import { API_BASE_URL } from '../config/api.config';

// Token module-level — set by AuthContext on login/logout, no localStorage race condition
let _authToken = null;
let _onUnauthorized = null;

export function setAuthToken(token) { _authToken = token; }
export function setOnUnauthorized(cb) { _onUnauthorized = cb; }

function getAuthToken() {
  return _authToken ?? (() => { try { return localStorage.getItem('aqua_token'); } catch { return null; } })();
}

function authHeaders(token = getAuthToken()) {
  const t = token;
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function handleStatus(res, requestToken) {
  if (res.status === 401) {
    // Ignore unauthenticated requests and stale requests from before a new login.
    if (requestToken && requestToken === getAuthToken()) _onUnauthorized?.();
    throw new Error('Sesión expirada. Por favor vuelve a iniciar sesión.');
  }
  if (!res.ok) throw new Error(`Error ${res.status}`);
  return res.json();
}

export async function apiGet(path) {
  const requestToken = getAuthToken();
  const res = await fetch(`${API_BASE_URL}${path}`, { credentials: 'omit', headers: { ...authHeaders(requestToken) } });
  return handleStatus(res, requestToken);
}

export async function apiPut(path, body) {
  const requestToken = getAuthToken();
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...authHeaders(requestToken) },
    body: JSON.stringify(body),
  });
  return handleStatus(res, requestToken);
}

export async function apiPost(path, body) {
  const requestToken = getAuthToken();
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders(requestToken) },
    body: JSON.stringify(body),
  });
  return handleStatus(res, requestToken);
}

export async function apiDelete(path) {
  const requestToken = getAuthToken();
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: 'DELETE',
    headers: { ...authHeaders(requestToken) },
  });
  return handleStatus(res, requestToken);
}

async function downloadErrorMessage(res) {
  let detail = null;
  try { detail = await res.json(); } catch { /* la respuesta no trae cuerpo JSON */ }
  const count = (value) => Number(value).toLocaleString('es-EC');
  if (detail?.error === 'export_too_large') {
    return `El rango seleccionado tiene ${count(detail.total)} registros y el máximo por archivo es ${count(detail.max)}. Reduce el rango de fechas.`;
  }
  if (detail?.error === 'invalid_range') {
    return 'El rango de fechas no es válido: la fecha inicial es posterior a la final.';
  }
  return detail?.error ? `Error ${res.status}: ${detail.error}` : `Error ${res.status}`;
}

export async function apiDownload(path) {
  const requestToken = getAuthToken();
  const res = await fetch(`${API_BASE_URL}${path}`, { credentials: 'omit', headers: { ...authHeaders(requestToken) } });
  if (res.status === 401) {
    if (requestToken && requestToken === getAuthToken()) _onUnauthorized?.();
    throw new Error('Sesión expirada. Por favor vuelve a iniciar sesión.');
  }
  if (!res.ok) throw new Error(await downloadErrorMessage(res));
  return {
    blob: await res.blob(),
    filename: res.headers.get('content-disposition')?.match(/filename="?([^";]+)"?/)?.[1],
  };
}

export async function getDefaultConfig() {
  return apiGet('/config/default-thresholds');
}

export async function saveDefaultConfig({ thresholds, refreshInterval }) {
  return apiPut('/config/default-thresholds', { thresholds, refreshInterval });
}

export async function getNotificationTimings() {
  return apiGet('/config/notification-timings');
}

export async function saveNotificationTimings(notificationTimings, notificationLevels) {
  return apiPut('/config/notification-timings', { notificationTimings, notificationLevels });
}

export async function notifyRoomSeen({ code, timestamp }) {
  try {
    await apiPost('/rooms/seen', { code, timestamp });
  } catch (e) {
    // avoid breaking UI on network errors
    console.warn('notifyRoomSeen failed:', e?.message || e);
  }
}

export async function getPersistHistory() {
  return apiGet('/config/persist-history');
}

export async function setPersistHistory(persistHistory) {
  return apiPut('/config/persist-history', { persistHistory });
}

export async function getHistoryConfig() {
  return apiGet('/config/history-config');
}

export async function setHistoryConfig(persistHistory, historyFrequency) {
  return apiPut('/config/history-config', { persistHistory, historyFrequency });
}

export async function getRoomHistory(code, metric, range) {
  return apiGet(`/rooms/${encodeURIComponent(code)}/history?metric=${encodeURIComponent(metric)}&range=${encodeURIComponent(range)}`);
}

// Incidents
export async function getIncidents(params = {}) {
  const qs = new URLSearchParams();
  if (params.room_code) qs.set('room_code', params.room_code);
  if (params.estado) qs.set('estado', params.estado);
  return apiGet(`/incidents?${qs.toString()}`);
}

export async function getIncident(id) {
  return apiGet(`/incidents/${id}`);
}

export async function createIncident(payload) {
  return apiPost('/incidents', payload);
}

export async function addIncidentComment(id, payload) {
  return apiPost(`/incidents/${id}/comments`, payload);
}

function texcumarQuery(params = {}) {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '' && value !== 'all') query.set(key, value);
  });
  return query.toString();
}

export function getTexcumarRooms() {
  return apiGet('/texcumar/rooms');
}

export function getTexcumarStats(params) {
  return apiGet(`/texcumar/stats?${texcumarQuery(params)}`);
}

export function getTexcumarSeries(params) {
  return apiGet(`/texcumar/series?${texcumarQuery(params)}`);
}

export function getTexcumarSummary(params) {
  return apiGet(`/texcumar/summary?${texcumarQuery(params)}`);
}

export function getTexcumarByRoom(params) {
  return apiGet(`/texcumar/by-room?${texcumarQuery(params)}`);
}

// Cabecera de flota: semáforo por estado, peor sala, tiempo bajo umbral y salas
// que dejaron de reportar. Trae tambien `data` con el estado de cada sala, para
// que la pantalla no tenga que pedir /by-room aparte y duplicar la consulta.
export function getTexcumarFleet(params) {
  return apiGet(`/texcumar/fleet?${texcumarQuery(params)}`);
}

/* Bandeja de alertas: los eventos que el dispositivo marcó como ALERT, con su
 * temperatura y su oxígeno ya evaluados contra la banda de cada sala. Va aparte
 * de /fleet porque se pide con su propio límite y se refresca al abrir el
 * panel, no en cada movimiento de filtro. */
export function getTexcumarAlerts(params) {
  return apiGet(`/texcumar/alerts?${texcumarQuery(params)}`);
}

/* Ventanas de mantenimiento. Lo declarado deja de contar para el semáforo y
 * para los promedios, pero las lecturas siguen enteras en el detalle y en el
 * Excel: silenciar una alarma no puede borrar lo que pasó. */
export function getTexcumarMantenimiento(params) {
  return apiGet(`/texcumar/mantenimiento?${texcumarQuery(params)}`);
}

export function crearTexcumarMantenimiento(ventana) {
  return apiPost('/texcumar/mantenimiento', ventana);
}

export function borrarTexcumarMantenimiento(id) {
  return apiDelete(`/texcumar/mantenimiento/${encodeURIComponent(id)}`);
}

export function getTexcumarOps() {
  return apiGet('/texcumar/ops');
}

export function saveTexcumarOps({ sinDatosMin, caidoMin }) {
  return apiPut('/texcumar/ops', { sinDatosMin, caidoMin });
}

// Catálogo de salas: nombres, umbrales y calibración. Es lo que edita la
// pantalla de configuración de Texcumar.
export function getTexcumarSalas() {
  return apiGet('/texcumar/salas');
}

export function saveTexcumarSalas(salas) {
  return apiPut('/texcumar/salas', { salas });
}

export function getTexcumarTrends(params) {
  return apiGet(`/texcumar/trends?${texcumarQuery(params)}`);
}

export function getTexcumarTelemetry(params) {
  return apiGet(`/texcumar/telemetry?${texcumarQuery(params)}`);
}

export function downloadTexcumarExport(params) {
  return apiDownload(`/texcumar/export?${texcumarQuery(params)}`);
}
